import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PostActions from '../PostActions/PostActions';
import InlineCommentNode from '../InlineCommentNode/InlineCommentNode';
import MarkdownRenderer from '../MarkdownRenderer/MarkdownRenderer';
import RichEditor from '../RichEditor';
import ComposerPreviewModal from '../ComposerPreviewModal/ComposerPreviewModal';
import { htmlToMarkdown } from '../RichEditor/richEditorUtils';
import { postsApi } from '../../services/api';
import hiveService from '../../services/hive';
import { usePostCache } from '../../context/PostCacheContext';
import { useAuth } from '../../context/AuthContext';
import { useLoginModal } from '../../context/LoginModalContext';
import { useActiveEditor } from '../../context/ActiveEditorContext';
import { useSettings } from '../../context/SettingsContext';
import { buildContentPath, buildFlexDetailPath, buildPostPath, getReplyCharacterLimit } from '../../utils/postUrls';
import { getPostAppInfo } from '../../utils/postApp';
import {
  buildOptimisticComment,
  buildOptimisticCommentUpsertPayload
} from '../../utils/optimisticComment';
import { buildOptimisticReplyBody } from '../../utils/optimisticReply';
import { buildSafePreviewText } from '../../utils/previewText';
import { formatRelativeTime, formatAbsoluteTime } from '../../utils/time';
import { buildCommentTree } from '../../utils/commentTree';
import useAuthorAvatar from '../../utils/useAuthorAvatar';
import { getFeedDisplayByAuthor } from '../../config/feed-details';
import { getHiveFrontendPattern } from '../../config/hiveFrontends';
import './CommentCard.css';

const CommentCard = ({ post, showActions = false, customActions = null, onCommentPosted, onOptimisticComment, onOptimisticCommentUpdate, onRetryOptimistic, isDetailView = false, showCommentToggle = true, hideCommentButton = false, onRepliesToggled, onExpandDeeper, mobileLimitPath }) => {
  const { getFromCache, addToCache } = usePostCache();
  const { user } = useAuth();
  const { openLoginModal } = useLoginModal();
  const { activeEditorId, openEditor, closeEditor } = useActiveEditor();
  const { commentDisplayMode, isUserMuted, removeMutedUser } = useSettings();
  const location = useLocation();
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [reblogMetadata, setReblogMetadata] = useState(null);
  const [reblogBody, setReblogBody] = useState('');
  const [reblogOriginalPost, setReblogOriginalPost] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyHtml, setReplyHtml] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [isCommentExpanded, setIsCommentExpanded] = useState(false);
  const [showMutedContent, setShowMutedContent] = useState(false);
  const [, setTimerTick] = useState(0); // Force re-renders for relative time updates
  const richEditorRef = useRef(null);
  const editorId = `comment-reply:${post.author}/${post.permlink}`;
  const showReply = activeEditorId === editorId;
  const replyCharLimit = getReplyCharacterLimit(post);
  const { avatarSources, handleAvatarError } = useAuthorAvatar({
    author: post?.author,
    profileImage: post?.profileImage
  });

  const handleReplyChange = (html) => {
    setReplyHtml(html);
    const markdown = htmlToMarkdown(html);
    setReplyText(markdown);
  };

  const updateOptimisticComment = useCallback((permlink, updates) => {
    if (onOptimisticCommentUpdate) {
      onOptimisticCommentUpdate(permlink, updates);
      return;
    }
    setComments(prev => prev.map(comment => comment.permlink === permlink ? { ...comment, ...updates } : comment));
  }, [onOptimisticCommentUpdate]);

  // mark whether author is muted (hook declarations come first so this is safe)
  const authorMuted = post && isUserMuted(post.author);

  const scheduleCommentConfirmationCheck = useCallback((author, permlink) => {
    const checkDelays = [20000, 60000];

    checkDelays.forEach((delay, index) => {
      setTimeout(async () => {
        const exists = await hiveService.checkPostExists(author, permlink);
        if (exists) {
          updateOptimisticComment(permlink, { optimistic: false, optimisticStatus: 'confirmed' });
          return;
        }
        if (index === checkDelays.length - 1) {
          updateOptimisticComment(permlink, { optimisticStatus: 'failed', optimisticError: 'Not found on-chain yet. Retry?' });
        }
      }, delay);
    });
  }, [updateOptimisticComment]);


  // if this comment was loaded from the server/cache with a pending optimistic
  // status we need to schedule the confirmation checks again.  without this
  // the page reload would leave the item stuck in "pending" indefinitely.
  useEffect(() => {
    if (post && post.optimistic && post.optimisticStatus === 'pending') {
      scheduleCommentConfirmationCheck(post.author, post.permlink);
    }
  }, [post, scheduleCommentConfirmationCheck]);

  // Update relative time display every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setTimerTick(prev => prev + 1);
    }, 30000); // Update every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  const handleRetryOptimisticLocal = async () => {
    if (post.optimisticType === 'short_post') {
      if (onRetryOptimistic) {
        onRetryOptimistic(post);
      }
      return;
    }

    if (!post.parentAuthor || !post.parentPermlink) return;

    try {
      updateOptimisticComment(post.permlink, { optimisticStatus: 'pending', optimisticError: '' });
      await hiveService.createComment(
        user?.username || post.author,
        post.parentAuthor,
        post.parentPermlink,
        post.body,
        post.rootAuthor || post.parentAuthor,
        { permlink: post.permlink }
      );
      // successful broadcast – persist our comment if not already stored
      await postsApi.upsertOptimisticShortPost(buildOptimisticCommentUpsertPayload({
        author: post.author,
        permlink: post.permlink,
        body: post.body,
        jsonMetadata: post.json_metadata,
        targetPost: post.parentAuthor && post.parentPermlink
          ? { author: post.parentAuthor, permlink: post.parentPermlink }
          : undefined,
        createdAt: post.created,
        voteCount: post.votes?.total || 0,
        replyCount: post.replies || 0
      }));
      scheduleCommentConfirmationCheck(post.author, post.permlink);
    } catch (error) {
      updateOptimisticComment(post.permlink, { optimisticStatus: 'failed', optimisticError: error.message || 'Retry failed' });
    }
  };

  // Extract embedded Hive post links from content
  // Ensure body is always a string
  const bodyText = typeof post.body === 'string' ? post.body : String(post.body || '');

  // Extract excerpt for long-form posts (> 700 chars)
  const COMMENTS_EXCERPT_LENGTH = 700;
  const isLongForm = bodyText.length > COMMENTS_EXCERPT_LENGTH;
  const commentPreview = buildSafePreviewText(bodyText, COMMENTS_EXCERPT_LENGTH);
  let displayText = bodyText;
  let showReadMore = false;

  if (isLongForm && !isDetailView && !isCommentExpanded) {
    // Try to get excerpt from metadata
    let excerpt = null;
    if (post.json_metadata) {
      const metadataExcerpt = typeof post.json_metadata === 'string'
        ? JSON.parse(post.json_metadata)
        : post.json_metadata;
      excerpt = metadataExcerpt.excerpt || metadataExcerpt.description;
    }

    if (excerpt) {
      displayText = excerpt;
      showReadMore = true;
    } else {
      displayText = commentPreview.text;
      showReadMore = commentPreview.isTruncated;
    }
  }

  // Save scroll position and feed context when navigating to post detail
  const handlePostLinkClick = () => {
    sessionStorage.setItem('scrollPosition', window.scrollY.toString());
    sessionStorage.setItem('returnPath', location.pathname);
    sessionStorage.setItem('preserveScroll', 'true');
    // Remember which post to focus when returning from detail view
    if (post && post.author && post.permlink) {
      sessionStorage.setItem('focusPost', `${post.author}/${post.permlink}`);
    }
  };

  const renderOptimisticStatus = () => {
    if (!post.optimistic && !post.is_pending && post.optimisticStatus !== 'confirmed') return null;

    const status = post.optimisticStatus === 'confirmed'
      ? 'confirmed'
      : (post.optimistic ? post.optimisticStatus : (post.is_pending ? 'failed' : null));
    if (status === 'failed') {
      return (
        <div className="optimistic-status failed">
          <span>Post not confirmed yet.</span>
          <button className="retry-optimistic-btn" onClick={handleRetryOptimisticLocal}>
            Retry
          </button>
        </div>
      );
    }

    if (status === 'confirmed') {
      return (
        <div className="optimistic-status confirmed">
          <span>Confirmed</span>
        </div>
      );
    }

    return (
      <div className="optimistic-status pending">
        <span>Posting...</span>
      </div>
    );
  };

  const fetchComments = async () => {
    if (comments.length > 0) {
      // Already fetched, just toggle
      setShowComments(!showComments);
      return;
    }

    setLoadingComments(true);
    try {
      // Check cache first
      const cached = getFromCache(post.author, post.permlink);
      if (cached && cached.commentsLoaded && Array.isArray(cached.comments)) {
        console.log('Using cached comments');
        const commentTree = buildCommentTree(cached.comments || [], post.author, post.permlink);
        setComments(commentTree);
        setShowComments(true);
        setLoadingComments(false);
        return;
      }

      // Fetch from API if not cached
      const data = await postsApi.getPostComments(post.author, post.permlink);
      
      // Cache the response
      addToCache(post.author, post.permlink, { post, comments: data.comments || [], commentsLoaded: true });
      
      const commentTree = buildCommentTree(data.comments || [], post.author, post.permlink);
      setComments(commentTree);
      setShowComments(true);
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoadingComments(false);
    }
  };

  const [expandedComments, setExpandedComments] = useState(new Set());

  const CARD_REPLY_DEPTH_LIMIT = parseInt(process.env.REACT_APP_CARD_REPLY_DEPTH_LIMIT, 10) || 3;

  const renderCommentCardNode = (comment, level = 0, parentExpanded = false) => {
    const key = `${comment.author}/${comment.permlink}`;
    const commentExpanded = expandedComments.has(key);
    const hasReplies = comment.replies && comment.replies.length > 0;
    const isAtLimit = level >= CARD_REPLY_DEPTH_LIMIT;
    const isInExtra = parentExpanded;
    const isAtExtraLimit = parentExpanded && level >= CARD_REPLY_DEPTH_LIMIT + 2;
    const handleExpand = () => {
      if (commentExpanded) {
        setExpandedComments(prev => { const s = new Set(prev); s.delete(key); return s; });
      } else {
        setExpandedComments(prev => new Set([...prev, key]));
      }
    };

    const showChildren = hasReplies && (
      commentExpanded ||
      (isInExtra && !isAtExtraLimit)
    );

    return (
      <div key={key} className={`comment-node level-${level}`}>
        <CommentCard
          post={comment}
          showActions={true}
          isDetailView={false}
          showCommentToggle={false}
          hideCommentButton={false}
          onExpandDeeper={hasReplies ? handleExpand : undefined}
          mobileLimitPath={isAtLimit && !isInExtra && !commentExpanded && hasReplies ? buildFlexDetailPath(comment.author, comment.permlink) : undefined}
          onCommentPosted={onCommentPosted}
          onRetryOptimistic={onRetryOptimistic}
        />
        {showChildren && (
          <div className="nested-replies">
            {comment.replies.map((r) => renderCommentCardNode(r, level + 1, isInExtra || (isAtLimit && commentExpanded)))}
          </div>
        )}
        {isAtLimit && !isInExtra && !commentExpanded && hasReplies && (
          <Link to={buildFlexDetailPath(comment.author, comment.permlink)} className="view-deeper-link show-on-mobile-only">
            Show deeper replies →
          </Link>
        )}
        {isAtExtraLimit && hasReplies && (
          <Link to={buildFlexDetailPath(comment.author, comment.permlink)} className="view-deeper-link">
            Show deeper replies →
          </Link>
        )}
      </div>
    );
  };

  // Extract images from metadata (for ImageCarousel)
  const extractImagesFromMetadata = (metadataObj) => {
    const images = [];
    
    // First priority: get images from json_metadata.image array or other possible fields
    if (metadataObj) {
      if (metadataObj.image && Array.isArray(metadataObj.image) && metadataObj.image.length > 0) {
        images.push(...metadataObj.image);
      } else if (metadataObj.images && Array.isArray(metadataObj.images) && metadataObj.images.length > 0) {
        // Alternative field name
        images.push(...metadataObj.images);
      } else if (metadataObj.thumbnail && typeof metadataObj.thumbnail === 'string') {
        // Single thumbnail image (might be from inleo or other sources)
        images.push(metadataObj.thumbnail);
      }
    }
    
    return images;
  };

  const toggleComments = () => {
    // Allow toggling even when there are no comments
    let newState;
    if (showComments) {
      setShowComments(false);
      newState = false;
    } else if (comments.length > 0) {
      setShowComments(true);
      newState = true;
    } else {
      // will open after fetch
      fetchComments();
      newState = true;
    }
    if (onRepliesToggled) {
      // notify parent CommentsSection so it can hide duplicate replies
      onRepliesToggled(post.author, post.permlink, newState);
    }
  };

  // Detect reblog from body only if it's just a single link
  const detectReblogFromBody = (body) => {
    if (!body) return null;
    const linkPattern = getHiveFrontendPattern();
    const match = body.match(linkPattern);
    
    if (match) {
      const trimmed = body.trim();
      if (trimmed !== match[0]) {
        return null;
      }

      return {
        author: match[1],
        permlink: match[2],
        body: '' // Will be fetched later
      };
    }
    return null;
  };

  // Parse json_metadata from multiple possible field names
  const parseJsonMetadata = () => {
    const rawMetadata = post.json_metadata ?? post.metadata ?? post.jsonMetadata ?? null;
    if (!rawMetadata) return null;
    if (typeof rawMetadata === 'string') {
      try {
        return JSON.parse(rawMetadata);
      } catch (error) {
        console.error('Error parsing json_metadata:', error);
        return null;
      }
    }
    return rawMetadata;
  };

  const metadata = parseJsonMetadata();
  const appInfo = getPostAppInfo(post, metadata);

  const formatCommunityName = (category) => {
    // Use community title from backend if available
    if (post.communityTitle) {
      return post.communityTitle;
    }
    
    if (category.startsWith('hive-')) {
      // Extract the part after 'hive-' and capitalize (fallback)
      const name = category.substring(5);
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
    return category;
  };

  const getCommunityData = () => {
    let category = post.category;
    let displayName = null;
    let link = null;

    if (category && category.startsWith('hive-')) {
      displayName = formatCommunityName(category);
      link = `/c/${category}`;
    } else if (post.tags && post.tags.length > 0) {
      category = post.tags[0];
      displayName = `#${category}`;
      link = `/tag/${category}`;
    } else if (category) {
      displayName = `#${category}`;
      link = `/tag/${category}`;
    }

    return displayName ? { displayName, link, isExternal: false } : null;
  };

  // Determine if this is short content (comment to container) or blog post
  const isShortContent = post.parentAuthor && post.depth > 0;
  // Use rootAuthor (for nested comments) or parentAuthor (for top-level comments) to determine feed
  const feedAuthor = post.rootAuthor || post.parentAuthor;
  const feedDisplay = getFeedDisplayByAuthor(feedAuthor);
  const feedData = isShortContent ? {
    name: feedDisplay.label,
    link: feedDisplay.path,
    isExternal: false
  } : null;
  const communityData = !isShortContent ? getCommunityData() : null;

  // Debug logging
  console.log('PostCard Debug:', {
    author: post.author,
    permlink: post.permlink,
    parentAuthor: post.parentAuthor,
    depth: post.depth,
    isShortContent,
    feedData,
    communityData
  });

  // Enhanced reblog detection - check multiple sources
  let reblogRaw = null;
  
  // Check metadata first
  if (metadata?.reblog_of) {
    reblogRaw = metadata.reblog_of;
  } else if (post.reblog_of) {
    reblogRaw = post.reblog_of;
  } else if (post.reblogOf) {
    reblogRaw = post.reblogOf;
  } else if (post.metadata?.reblog_of) {
    reblogRaw = post.metadata.reblog_of;
  } else if (!reblogRaw && post.body) {
    // Fallback: check if body is just a reblog link
    const bodyReblog = detectReblogFromBody(post.body);
    if (bodyReblog) {
      reblogRaw = bodyReblog;
    }
  }
  
  const reblogSource = typeof reblogRaw === 'string'
    ? (() => {
        try {
          return JSON.parse(reblogRaw);
        } catch (error) {
          console.error('Error parsing reblog data:', error);
          return null;
        }
      })()
    : reblogRaw;

  const reblogData = reblogSource ? {
    originalAuthor: reblogSource.author,
    originalPermlink: reblogSource.permlink,
    originalBody: reblogSource.body || post.rebloggedBody || post.originalBody || (typeof post.body === 'string' ? post.body : ''),
    commentary: reblogSource.commentary || ''
  } : null;

  console.log('DEBUG PostCard: reblogData full object:', reblogData);
  
  // Fetch reblog metadata and body
  React.useEffect(() => {
    if (!reblogData?.originalAuthor || !reblogData?.originalPermlink) return;
    
    // Only fetch if we don't have body yet
    if (reblogBody) return;

    const fetchReblogData = async () => {
      try {
        const cached = getFromCache(reblogData.originalAuthor, reblogData.originalPermlink);
        if (cached?.body) {
          setReblogOriginalPost(cached);
          setReblogBody(cached.body);
          if (cached.json_metadata) {
            setReblogMetadata(typeof cached.json_metadata === 'string' ? JSON.parse(cached.json_metadata) : cached.json_metadata);
          }
          return;
        }
        
        const data = await postsApi.getPost(reblogData.originalAuthor, reblogData.originalPermlink, false);
        if (data?.post?.body) {
          addToCache(reblogData.originalAuthor, reblogData.originalPermlink, data.post);
          setReblogOriginalPost(data.post);
          setReblogBody(data.post.body);
          if (data.post.json_metadata) {
            setReblogMetadata(typeof data.post.json_metadata === 'string' ? JSON.parse(data.post.json_metadata) : data.post.json_metadata);
          }
        }
      } catch (error) {
        console.error('Error fetching reblog data:', error);
      }
    };

    fetchReblogData();
  }, [reblogData?.originalAuthor, reblogData?.originalPermlink, reblogBody, getFromCache, addToCache]);

  const displayReblogBody = reblogBody || reblogData?.originalBody || '';
  const reblogTargetHref = buildContentPath(reblogData?.originalAuthor, reblogData?.originalPermlink, reblogOriginalPost || reblogData);
  const previewReplyRawBody = replyHtml && replyHtml.trim() ? htmlToMarkdown(replyHtml) : replyText;
  const previewReplyBody = user
    ? hiveService.appendUserSignatureForContext(previewReplyRawBody, user.username, 'comment')
    : previewReplyRawBody;
  const previewReplyPost = user ? {
    author: user.username,
    permlink: 'preview-reply',
    body: previewReplyBody,
    created: new Date().toISOString(),
    json_metadata: JSON.stringify({ app: 'flexus' }),
    parentAuthor: post.author,
    parentPermlink: post.permlink,
    rootAuthor: post.rootAuthor || post.parentAuthor || post.author,
    depth: 1,
    votes: { total: 0, up: 0 },
    replies: 0,
  } : null;

  const handleSubmitReply = async () => {
    if (!user) {
      openLoginModal();
      return;
    }

    const { normalizedReply, optimisticReplyBody } = buildOptimisticReplyBody({
      replyHtml,
      replyText,
      username: user.username
    });
    setIsReplying(true);
    try {
      const permlink = hiveService.generatePermlinkForReply(post.author, post.rootAuthor || post.parentAuthor);
      const createdAt = new Date().toISOString();
      const jsonMetadata = hiveService.buildCommentMetadata(user.username, normalizedReply, post.author, post.parentAuthor, permlink);

      const optimisticComment = buildOptimisticComment({
        author: user.username,
        permlink,
        body: optimisticReplyBody,
        createdAt,
        jsonMetadata,
        targetPost: { author: post.author, permlink: post.permlink },
        rootAuthor: post.parentAuthor || post.author,
        depth: 1
      });

      if (onOptimisticComment) {
        onOptimisticComment(optimisticComment);
      } else {
        setComments(prev => [optimisticComment, ...prev]);
        setShowComments(true);
      }

      try {
        await hiveService.createComment(
          user.username,
          post.author,
          post.permlink,
          normalizedReply,
          post.rootAuthor || post.parentAuthor,
          { permlink }
        );

        await postsApi.upsertOptimisticShortPost(buildOptimisticCommentUpsertPayload({
          author: user.username,
          permlink,
          body: optimisticReplyBody,
          jsonMetadata,
          targetPost: { author: post.author, permlink: post.permlink },
          createdAt
        }));

        setReplyText('');
        setReplyHtml('');
        setPreviewOpen(false);
        closeEditor(editorId);
        alert('Reply posted successfully!');
        scheduleCommentConfirmationCheck(user.username, permlink);
      } catch (err) {
        if (onOptimisticCommentUpdate) {
          onOptimisticCommentUpdate(permlink, {
            optimisticStatus: 'pending',
            optimisticError: err.message || 'Broadcast returned an error, verifying on-chain...'
          });
        } else {
          setComments(prev => prev.map(c => c.permlink === permlink
            ? { ...c, optimisticStatus: 'pending', optimisticError: err.message || 'Broadcast returned an error, verifying on-chain...' }
            : c));
        }
        console.warn('Reply broadcast returned error, verifying on-chain status:', err?.message || err);
        scheduleCommentConfirmationCheck(user.username, permlink);
      }
    } catch (error) {
      alert('Failed to post reply: ' + error.message);
    } finally {
      setIsReplying(false);
    }
  };

  if (authorMuted && !showMutedContent) {
    return (
      <div className="comment-card" data-postid={`${post.author}/${post.permlink}`}>
        <div className="post-header">
          <div className="header-content">
            <Link to={`/user/${post.author}`} className="author-link">
              <img
                src={avatarSources[0] || ''}
                alt={post.author}
                className="author-avatar"
                data-avatar-fallback-index="0"
                onError={handleAvatarError}
              />
              <span className="author-name">{post.author}</span>
            </Link>
          </div>
        </div>
        <div className="post-body">
          <div className="markdown-renderer markdown-content muted-user">
            <p style={{ display: 'inline-block' }}>Muted content</p>
            <button className="btn showmuted-btn" style={{ display: 'inline-block', float: 'right' }} onClick={() => setShowMutedContent(true)}>Show content</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="comment-card" data-postid={`${post.author}/${post.permlink}`}>
      {/* Label in top-right corner - Always show reply label for comments */}
      {post.parentAuthor && (
        <span className="post-label reply-label">
          Reply to @{post.parentAuthor}
        </span>
      )}

      {reblogData ? (
        // Reblog display - outer container with reblogger's avatar, inner with original post
        <>
          <div className="post-header">
            <div className="header-content">
              <Link to={`/user/${post.author}`} className="author-link">
                <img 
                  src={avatarSources[0] || ''}
                  alt={post.author}
                  className="author-avatar"
                  data-avatar-fallback-index="0"
                  onError={handleAvatarError}
                />
                <span className="author-name">{post.author}</span>
              </Link>
              <span className="post-time" title={formatAbsoluteTime(post.created)}>{formatRelativeTime(post.created)}</span>
              {appInfo && (
                <span className="post-via-app" title={appInfo.key}>
                  via <em>{appInfo.name}</em>
                </span>
              )}
            </div>
          </div>
          {renderOptimisticStatus()}
          {reblogData?.commentary && (
            <div className="post-body">
              <p>{reblogData.commentary}</p>
            </div>
          )}
          <div className="reblog-label">reblogged</div>
          <div className="reblog-content">
            <div className="reblog-original-header">
              <Link to={`/user/${reblogData.originalAuthor}`} className="original-author">
                @{reblogData.originalAuthor}
              </Link>
            </div>
            {displayReblogBody && displayReblogBody.trim() ? (
              (() => {
                // Use metadata from fetched original post
                const images = extractImagesFromMetadata(reblogMetadata);
                return (
                  <div className="reblog-markdown-content">
                    <MarkdownRenderer content={displayReblogBody} images={images} reblogData={reblogData} />
                  </div>
                );
              })()
            ) : (
              <div className="reblog-markdown-content">
                <div className="post-body">Loading original content...</div>
              </div>
            )}
            {!isDetailView && (
              <div className="reblog-actions">
                <Link
                  to={reblogTargetHref}
                  className="view-reblog-link"
                  onClick={(e) => {
                    sessionStorage.setItem('scrollPosition', window.scrollY.toString());
                    sessionStorage.setItem('returnPath', location.pathname);
                    sessionStorage.setItem('preserveScroll', 'true');
                  }}
                >
                  View original post →
                </Link>
              </div>
            )}
          </div>
        </>
      ) : (
        // Regular post display with avatar
        <>
          <div className="post-header">
            <div className="header-content">
              <Link to={`/user/${post.author}`} className="author-link">
                <img 
                  src={avatarSources[0] || ''}
                  alt={post.author}
                  className="author-avatar"
                  data-avatar-fallback-index="0"
                  onError={handleAvatarError}
                />
                <span className="author-name">{post.author}</span>
              </Link>
              <span className="post-time" title={formatAbsoluteTime(post.created)}>{formatRelativeTime(post.created)}</span>
              {appInfo && (
                <span className="post-via-app" title={appInfo.key}>
                  via <em>{appInfo.name}</em>
                </span>
              )}
              {authorMuted && showMutedContent && (
                <div className="muted-content-controls">
                  <button className="btn user-unmute-btn" style={{ fontSize: '0.6rem', padding: '6px' }}
                    onClick={() => { if (window.confirm(`Unmute @${post.author}?`)) { removeMutedUser(post.author); } }}
                  >Unmute user</button>
                  <button className="btn hide-mutebtn" style={{ marginLeft: '8px', fontSize: '0.6rem', padding: '6px' }}
                    onClick={() => setShowMutedContent(false)}
                  >Hide content</button>
                </div>
              )}
            </div>
          </div>
          {renderOptimisticStatus()}

          <div className="post-body">
            {(() => {
              let postMetadata = null;
              if (post.json_metadata) {
                if (typeof post.json_metadata === 'string') {
                  try {
                    postMetadata = JSON.parse(post.json_metadata);
                  } catch (e) {}
                } else {
                  postMetadata = post.json_metadata;
                }
              }
              const images = extractImagesFromMetadata(postMetadata);
              return <MarkdownRenderer content={displayText} images={images} reblogData={reblogData} />;
            })()}
            <div className="short-content-controls">
              {isLongForm && !isDetailView && (
                <button
                  type="button"
                  className="short-content-expand-btn"
                  onClick={() => setIsCommentExpanded((prev) => !prev)}
                >
                  {isCommentExpanded ? 'Collapse' : 'Expand'}
                </button>
              )}

              {showReadMore && (
                <Link
                  to={buildPostPath(post.author, post.permlink)}
                  className="read-more-indicator"
                  onClick={handlePostLinkClick}
                >
                  ... Read more
                </Link>
              )}
            </div>
            {authorMuted && showMutedContent && (
              <div className="muted-content-controls">
                <button className="btn hide-mutebtn" style={{ fontSize: '0.8rem', padding: '8px 14px' }}
                  onClick={() => setShowMutedContent(false)}
                >Hide content</button>
              </div>
            )}
          </div>
        </>
      )}

      {showActions && (
        <>
          <PostActions 
            post={post} 
            onCommentPosted={onCommentPosted} 
            hideCommentButton={onExpandDeeper ? false : hideCommentButton}
            onReplyClick={() => (showReply ? closeEditor(editorId) : openEditor(editorId))}
            showReply={showReply}
            onCommentToggle={onExpandDeeper || toggleComments}
            commentsCount={comments.length > 0 ? comments.length : (Array.isArray(post.replies) ? post.replies.length : (post.replies || 0))}
            loadingComments={loadingComments}
            mobileLimitPath={mobileLimitPath}
          />

          {showReply && (
            <div className="reply-box">
              {user ? (
                <>
                  <RichEditor
                    ref={richEditorRef}
                    value={replyHtml}
                    onChange={handleReplyChange}
                    onSubmit={handleSubmitReply}
                    placeholder="Write your reply..."
                    maxLength={replyCharLimit}
                    toolbar={true}
                  />
                  <div className="reply-actions">
                    <div className="reply-meta-actions">
                      {replyCharLimit && <span className="char-count">{replyText.length}/{replyCharLimit}</span>}
                      <button
                        className="preview-reply-btn"
                        onClick={() => setPreviewOpen(true)}
                        disabled={!replyText.trim() || isReplying}
                      >
                        Preview
                      </button>
                    </div>
                    <div className="reply-buttons">
                      <button 
                        className="cancel-reply-btn"
                        onClick={() => {
                          setPreviewOpen(false);
                          closeEditor(editorId);
                          setReplyText('');
                          setReplyHtml('');
                        }}
                      >
                        Cancel
                      </button>
                      <button 
                        className="submit-reply-btn"
                        onClick={handleSubmitReply}
                        disabled={!replyText.trim() || isReplying}
                      >
                        {isReplying ? 'Posting...' : 'Reply'}
                      </button>
                    </div>
                  </div>

                  <ComposerPreviewModal
                    isOpen={previewOpen}
                    title="Preview Reply"
                    content={previewReplyBody}
                    previewNode={previewReplyPost ? <CommentCard post={previewReplyPost} showActions={false} showCommentToggle={false} hideCommentButton={true} /> : null}
                    author={user?.username || post.author}
                    focusTargetRef={richEditorRef}
                    submitLabel="Reply"
                    isSubmitting={isReplying}
                    onClose={() => setPreviewOpen(false)}
                    onKeepWriting={() => {
                      setPreviewOpen(false);
                      window.setTimeout(() => {
                        richEditorRef.current?.focus?.();
                      }, 0);
                    }}
                    onSubmit={handleSubmitReply}
                  />
                </>
              ) : (
                <div className="reply-login-prompt">
                  <p>Login to write a reply.</p>
                  <div className="reply-buttons">
                    <button
                      className="cancel-reply-btn"
                      onClick={() => closeEditor(editorId)}
                    >
                      Cancel
                    </button>
                    <button
                      className="submit-reply-btn"
                      onClick={openLoginModal}
                    >
                      Login to Reply
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {showComments && !isDetailView && (
            <div className="inline-comments-section">
              {loadingComments ? (
                <div className="comments-loading">
                  <div className="loading-dots">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </div>
                  <p>Loading comments...</p>
                </div>
              ) : (
                <>
                  <div className="comments-header">
                    <h4>Comments</h4>
                    {comments.length > 3 && (
                      <Link to={buildContentPath(post.author, post.permlink, post)} className="view-all-link" onClick={handlePostLinkClick}>
                        View all {comments.length} comments →
                      </Link>
                    )}
                  </div>
                  {comments.length === 0 ? (
                    <div className="no-comments-container">
                      <p className="no-comments">No comments yet</p>
                      {user && (
                        <button 
                          className="be-first-btn"
                          onClick={() => openEditor(editorId)}
                        >
                          ✍️ Be the first to comment
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="inline-comments-list">
                      {comments.slice(0, 3).map((comment, index) => (
                        commentDisplayMode === 'inline' ? (
                          <InlineCommentNode 
                            key={`${comment.author}-${comment.permlink}-${index}`}
                            comment={comment}
                            depth={0}
                            maxDepth={parseInt(process.env.REACT_APP_CARD_REPLY_DEPTH_LIMIT, 10) || 3}
                            postAuthor={post.author}
                            postPermlink={post.permlink}
                          />
                        ) : (
                          renderCommentCardNode(comment, 0)
                        )
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}

      {customActions ? (
        <div className="post-actions-custom-container">
          {customActions}
        </div>
      ) : (
        !showActions && showCommentToggle && !isDetailView && (
          <div className="post-stats">
            <button className="stat comment-toggle-btn" onClick={toggleComments}>
              💬 {post.replies || 0} {loadingComments ? '...' : (showComments ? '(Hide)' : '(Show)')}
            </button>
            <span className="stat">⬆ {post.votes?.up || 0}</span>
          </div>
        )
      )}
    </div>
  );
};

export default React.memo(CommentCard);
